<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { ContentsContext, Feature, PageName } from '@/api/tracking';
import noImage1to1 from '@/assets/noimage-1to1.jpg';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import DocumentSentenceDisplay from '@/components/common/adp-document/document-sentence-display.vue';
import CitationBadge from '@/components/common/atoms/citation-badge.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import { AdpDocument, Report } from '@/types';
import { useSurveyDocumentActions } from '@/utils/actions/useSurveyDocumentActions';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getHighlightKeywords } from '@/utils/adpDocument';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { imageErrorHandler } from '@/utils/errorHandler';

interface Props {
  adpReport: Report;
  badgeNumber: number;
  /* これ以下は計測で使用 */
  pageName: PageName;
  feature: Feature;
  rankInWholeFeed: number;
  contentsContext: ContentsContext;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-document': [AdpDocument];
}>();

const { getCurrentPathForTracking } = useCurrentPathForTracking();

const { adpReport } = toRefs(props);

const {
  actionModal,
  isPostingComment,
  isMarked,
  markedUsers,
  markedGroups,
  commentedUsers,
  optionActions,
  handleOptionAction,
  handleCommentButton,
  handleGroupMarkButton,
  handlePopupMenu,
  closeActionModal,
  handleMarkButton,
  groupMarkArticle,
  commentArticle,
} = useSurveyDocumentActions(
  adpReport,
  {
    pageName: props.pageName,
    feature: props.feature,
    rankInWholeFeed: props.rankInWholeFeed,
    contentsContext: props.contentsContext,
  },
  emit,
);

const imageUrl = computed(() => props.adpReport.image_url || noImage1to1);

const highlightKeywords = computed(() => getHighlightKeywords(props.adpReport));

const sendViewProps = computed(() => ({
  adpDocument: props.adpReport,
  rankInWholeFeed: props.rankInWholeFeed,
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: props.feature,
  },
  contentsContext: props.contentsContext,
}));
</script>

<template>
  <div class="report-card">
    <div class="report-site-name c-text-regular--s">
      <span class="doc-type-label c-title-bold--xs">
        <CitationBadge :doc-type="adpReport.doc_type" :number="badgeNumber" />
      </span>
      <div class="report-page-number">
        <div>
          <DgrIcon name="file-pdf-fill-color" />
        </div>
        <div class="spacing-02"></div>
        <template v-if="adpReport.page_number">
          <div class="page-number">ページ：{{ adpReport.page_number }}</div>
        </template>
      </div>
      <div class="spacing-04"></div>
      <div class="report-site-name-text">{{ adpReport.site_name }}</div>
    </div>
    <div class="spacing-08"></div>
    <div class="report-content-wrapper">
      <div class="content">
        <div class="report-title c-title-bold--xm">
          <AdpDocumentCardLink
            :adp-document="adpReport"
            @article-link-clicked="
              articleClickedEvent(sendViewProps, adpReport)
            "
            @article-link-context-menu="
              articleContextMenuClickedEvent(sendViewProps, $event, adpReport)
            "
          >
            {{ adpReport.title }}
          </AdpDocumentCardLink>
        </div>
        <div class="spacing-04"></div>
        <DocumentSentenceDisplay
          v-if="adpReport.chunk_text"
          :text="adpReport.chunk_text ?? ''"
          :highlight-keywords="highlightKeywords"
          :line-clamp="2"
        ></DocumentSentenceDisplay>
      </div>
      <div class="report-image">
        <AdpDocumentCardLink
          :adp-document="adpReport"
          @article-link-clicked="articleClickedEvent(sendViewProps, adpReport)"
          @article-link-context-menu="
            articleContextMenuClickedEvent(sendViewProps, $event, adpReport)
          "
        >
          <img
            class="report-thumbnail"
            loading="lazy"
            :src="imageUrl"
            @error="imageErrorHandler($event, '3to1')"
          />
        </AdpDocumentCardLink>
      </div>
    </div>
    <div class="spacing-16"></div>
    <div class="report-user-action">
      <AdpDocumentActionBar
        :adp-document="adpReport"
        :is-marked="isMarked"
        :marked-users="markedUsers"
        :marked-groups="markedGroups"
        :commented-users="commentedUsers"
        :option-actions="optionActions"
        @option-action="handleOptionAction"
        @handle-popup-menu-click="handlePopupMenu"
        :mark-handler="handleMarkButton"
        @handle-group-mark-click="handleGroupMarkButton"
        @handle-comment-click="handleCommentButton"
      />
    </div>
    <MarkModal
      v-if="actionModal === 'mark'"
      :adp-document="adpReport"
      :page-name="pageName"
      :feature="feature"
      @submit="handleMarkButton"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-if="actionModal === 'group_mark'"
      :adp-document="adpReport"
      :page-name="pageName"
      :feature="feature"
      @submit="groupMarkArticle"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-if="actionModal === 'comment'"
      :adp-document="adpReport"
      :page-name="pageName"
      :feature="feature"
      :is-posting-comment="isPostingComment"
      @submit="commentArticle"
      @close="closeActionModal"
    ></CommentCreateModal>
  </div>
</template>

<style lang="scss" scoped>
.report-card {
  width: 100%;
  background-color: white;
  padding: 16px;
  column-gap: 16px;
  box-sizing: border-box;
  border: 1px solid $color-gray400;
  border-radius: 4px;

  .report-page-number {
    display: flex;
    align-items: center;
    .page-number {
      white-space: nowrap;
      padding-right: 10px;
      border-right: 1px solid $color-gray400;
    }
    .number-badge {
      margin-left: 10px;
    }
  }

  .report-thumbnail {
    max-width: 48px;
    object-fit: contain;
    font-weight: 400;
  }
  .report-site-name {
    color: $color-gray800;
    font-weight: 500;
    display: flex;
    align-items: center;
    .report-site-name-text {
      max-width: 155px;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
    }
    .doc-type-label {
      margin-right: 6px;
    }
    .animation-delay {
      animation: text-animation 1s steps(1, end) forwards;
      opacity: 0;
    }
    @keyframes text-animation {
      to {
        opacity: 1;
      }
    }
  }
  .report-title {
    overflow: hidden;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    word-break: break-all;
    font-weight: 500;
    a:hover {
      text-decoration: underline;
    }
  }

  .report-content-wrapper {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    .content {
      flex: 1;
    }
  }

  .report-user-action {
    margin-top: auto;
  }
}
</style>
